import { FLAMECO_AUTH_BASE_URL } from '../constants/flamecoAuth';
import { authFetch } from '../flameco/flameco-auth';
import { ApiException } from '../types/flameco';
import { RbacConfig } from '../types/auth';

/**
 * Typed client for the shared RBAC config backend (customer_portal_service
 * /rbac-config). One shared, team-wide document: role→module permissions +
 * per-user role overrides. Same auth contract as the other internal-tools
 * clients (Flameco JWT, Supabase fallback; 401 clears the session).
 */

const BASE = `${FLAMECO_AUTH_BASE_URL}/rbac-config`;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await authFetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch {
    throw new ApiException(0, 'Network error — check your connection and try again');
  }

  if (res.status === 401) {
    throw new ApiException(401, 'Your session expired. Please sign in again.');
  }

  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : body.message;
    } catch {
      /* ignore */
    }
    throw new ApiException(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

interface RbacRow {
  id: string;
  config: RbacConfig;
  updatedByEmail: string | null;
  updatedAt: string;
}

/** Returns the shared RBAC config, or null if never saved (fresh DB). */
export async function getConfig(): Promise<RbacConfig | null> {
  const row = await request<RbacRow | null>('');
  return row?.config ?? null;
}

export async function saveConfig(config: RbacConfig, updatedByEmail?: string): Promise<void> {
  await request<RbacRow>('', {
    method: 'PUT',
    body: JSON.stringify({ config, updatedByEmail }),
  });
}
